import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import {
  CLEARED_SENSITIVE_NOTE,
  PARTICIPANTS_CHANGED_NOTICE,
  bookerParticipantIdOf,
  isEmptyAnswer,
  isFieldVisible,
  layoutFields,
  layoutOrder,
  prefillLabel,
  savedSensitiveNote,
  participantIdOfReply,
  participantIdsOf,
  privacyNote,
  rowKeyOf,
  saveBookingParticipantRow,
  sensitiveRuns,
  withReturnedIds,
  firstNameKey,
  isNameKey,
  nameMarked,
  nameRuleGuards,
  notComingChosen,
  notComingDone,
  notComingFirst,
  notComingNumbered,
  notComingRemovals,
  participantIdOf,
  renameAskMessage,
  replacedIdOf,
  replaceSaveRow,
  riderNameOf,
  tombstoneFor,
  type AnswerRow,
  type BookerContact,
  type GuestNoun,
  type QuestionField,
} from "@vertu/shared";
import { supabase } from "../config";
import {
  DEFAULT_NOUN,
  EMPTY_DRAFT,
  adoptSavedIds,
  adoptedAndChanged,
  allRiderFields,
  answerRename,
  askRename,
  notComingOfForm,
  renameNeeded,
  renameStage,
  renamesToAsk,
  storedRiderOf,
  type RenameAsk,
  applyAnswer,
  asksAnything,
  carryRowKeys,
  clearedKey,
  checkoutReady,
  commitPlan,
  describeRowsRefusal,
  describeSaveRefusal,
  discardRows,
  groupVisits,
  mergeDraft,
  normaliseForm,
  nounTitle,
  planRowKeys,
  prefillStillOffered,
  prefillWithdrawnMessage,
  riderOne,
  riderRowPayload,
  savedGroupKeys,
  savedRowKeys,
  setCleared,
  setRiderOnePrefill,
  storedAnswersValid,
  syncRiderOnePrefill,
  typedRow,
  visitIssue,
  withRowKeys,
  type CheckoutDraft,
  type CheckoutForm,
  type CheckoutVisit,
  type DraftRow,
  type DraftValue,
} from "../state/booking-questions-form";
import { QuestionControl } from "./booking/QuestionControl";
import {
  PRIVATE_PROVIDED,
  answerElementId,
  firstIncompleteRider,
  groupProblemKey,
  riderIssue,
  riderProblem,
  riderReview,
  riderSlots,
  scopeVisits,
  slotForm,
  type RiderReview,
  type RiderSlot,
  type SaveScope,
} from "../state/checkout-steps";
import {
  ADOPTED_CHANGED_NOTE,
  DRAWER_SAVE_BUTTON,
  NO_SAVED_RIDERS,
  RIDER_SAVE_TIMEOUT_MS,
  afterRead,
  changedWhileSavingMessage,
  draftAfterRiderSaved,
  expectedRow,
  formAfterRiderSaved,
  isRiderSaved,
  landedOwnSaveOf,
  leaveAfterSave,
  leaveBeforeSave,
  markNothingToSend,
  markSaved,
  nothingHeldToClear,
  resumeRiderIndex,
  resumeSavedNotice,
  retryButton,
  riderHasNothingToSend,
  riderOneSavedNote,
  ridersChangedMessage,
  riderSaveProblem,
  riderTitle,
  riderTitlesOf,
  ridersToSave,
  rowFingerprint,
  savedFlags,
  saveStopShort,
  sentAsSaved,
  unsavedChangesNote,
  type LeaveOutcome,
  type RiderSaveProblem,
  type RiderSaveResult,
  type SavedRiders,
} from "../state/rider-autosave";
import { LAPSE_MESSAGE } from "../state/hold-timer-logic";
import { RiderTabs } from "./checkout/RiderTabs";
import { NotComingChooser, RenamePrompt, ReplacingNote } from "./checkout/RiderRename";

export type { RiderSaveResult };

/**
 * What the checkout calls on Proceed. commit() writes every unsaved answer,
 * then the ticked statements (accept_checkout_confirmations), and returns the
 * guest-facing reason it stopped, or null when the payment may start.
 */
export type ParticipantDetailsHandle = {
  commit: () => Promise<string | null>;
  /** Read the forms again (the server refused the payment for a missing answer). */
  reload: () => void;
  /** The first answer or tick still missing, worded for the guest (null: none). */
  issue: () => string | null;
  /** The noun the forms use ("rider"), for the checkout's own messages. */
  noun: GuestNoun;
  /** The full-page checkout (CheckoutPage): one rider's first missing answer, or null. */
  riderIssue: (index: number) => string | null;
  /** The full-page checkout: the first rider still missing something, or -1. */
  firstIncompleteRider: () => number;
  /**
   * The full-page checkout: take the guest to the first question still
   * missing an answer — a rider's (by index) or a visit's once-per-booking
   * question ("group") — and say whether there was one to go to.
   */
  focusProblem: (target: { rider: number } | "group") => boolean;
  /**
   * Save one part with today's whole-list writers: "rows" (every rider,
   * save_booking_participants) or "group" (save_booking_group_details, the
   * full page's end of Booking details). The reason it stopped is returned
   * for the page to show beside its button (the forms do not show it a
   * second time).
   */
  save: (scope: "rows" | "group") => Promise<string | null>;
  /**
   * The full-page checkout: save ONE rider (save_booking_participant_row),
   * the one on screen as the guest taps Next, Back or another rider's chip
   * (the drawer's pager does the same from inside). A rider already saved
   * as the card shows it, or with nothing to send, is not sent (sent:
   * false); one still missing something is never sent (I1: "incomplete").
   * Anything else is a RiderSaveProblem in plain words, the answers left on
   * the card and the rider unticked.
   */
  saveRider: (index: number, opts?: { again?: string }) => Promise<RiderSaveResult>;
  /**
   * The full-page checkout's end of the Riders step: every complete rider
   * not saved yet, one at a time. null once every one is in; else the first
   * rider that could not be saved, and why. `again`: the button that tries
   * again ("Continue", "Pay").
   */
  saveRiders: (again?: string) => Promise<{ index: number; problem: RiderSaveProblem } | null>;
  /** The full-page checkout: is this rider saved as the card shows it (the tick)? */
  riderSaved: (index: number) => boolean;
  /** The full-page checkout: is "use my details" (Yes, I'm riding) on for this rider? */
  prefillOn: (index: number) => boolean;
};

/** What the forms hold, for the full-page checkout's summary, progress and Review (memory only). */
export type ParticipantSummary = {
  loaded: boolean;
  noun: GuestNoun;
  /** saved: the server holds this rider as the card shows it (the full page's tick). */
  riders: (RiderSlot & RiderReview & { saved: boolean })[];
  /** A visit asks questions once for the whole booking. */
  bookingDetails: boolean;
  /** A visit has statements to tick. */
  confirmations: boolean;
};

/** Which part of the forms the full-page checkout shows on its current step. */
export type SteppedForms = {
  part: "prefill" | "riders" | "booking" | "review";
  /** The rider card on screen (an index into riderSlots). */
  riderIndex: number;
  onRiderIndex: (index: number) => void;
  /** A phone: numbered chips; wider: tabs named "Rider 1"… */
  compact: boolean;
  /** The line a rider card belongs to, as the page shows it ("To the Top", "Thu 1 Oct · 08:30"). */
  lineInfo?: (bookingId: string) => { title: string; when: string | null } | null;
  /** Review's Edit links. */
  onEdit?: (target: { step: "riders"; riderIndex: number } | { step: "booking" }) => void;
  /** The page's own words for the card on screen (Next found something missing). */
  riderMessage?: string | null;
  /** A one-rider save found the hold ended (not_authorized / hold_expired): Pay and the steps stop. */
  onLapse?: () => void;
  /** The guest answered what a card asked (P4a): the page's words about it go. */
  onAnswered?: () => void;
};

/**
 * Per cart (its token), per rider card (RiderSlot.key): the rows sent in
 * one-rider saves whose reply never came (the signal, or the time limit).
 * Any of them may have been stored: a read that finds one there knows it for
 * this checkout's own (landedOwnSaveOf), never someone else's. Kept for the
 * page's life, across the drawer closing and opening again, so a save still
 * on its way when the drawer closed is recognised when it lands. Page memory
 * only (gone on a reload); never sessionStorage or localStorage.
 */
const lostSaves = new Map<string, Record<string, AnswerRow[]>>();
function lostSavesOf(cartToken: string): Record<string, AnswerRow[]> {
  let rows = lostSaves.get(cartToken);
  if (!rows) {
    rows = {};
    lostSaves.set(cartToken, rows);
  }
  return rows;
}

/** The cart's booking forms, as the load and the pre-save check read them. */
export async function readCartForms(cartToken: string): Promise<CheckoutForm[]> {
  const { data, error } = await supabase.rpc("get_cart_booking_participants", { p_cart_token: cartToken });
  if (error || data?.ok !== true || !Array.isArray(data.bookings)) throw new Error("load");
  return (data.bookings as unknown[])
    .map(normaliseForm)
    .filter((form): form is CheckoutForm => form !== null);
}

/**
 * Booking questions at checkout: each guest's answers, the once-per-booking
 * answers and the statements to tick, grouped by visit (one service at one
 * start). Booking-specific fields are snapshots, so later operator edits
 * cannot change a guest's form. What is typed here lives only in this
 * component's memory until it is saved to the hold.
 */
/** "Removed Ann Rider (was Rider 2) from this booking.": takes the focus once a removal is done (P4a). */
const NOT_COMING_DONE_ID = "participant-notcoming-done";

export function ParticipantDetails({
  cartToken,
  signature,
  onValidityChange,
  handleRef,
  booker,
  venueName,
  stepped,
  paged = false,
  hold,
  payName = "Pay",
  onSummary,
}: {
  cartToken: string;
  signature: string;
  onValidityChange: (valid: boolean) => void;
  handleRef?: MutableRefObject<ParticipantDetailsHandle | null>;
  /** The checkout's own contact fields: "use my details for Rider 1" copies them. */
  booker?: BookerContact;
  /** The venue, for "Only Demo's managers see these answers." */
  venueName?: string | null;
  /**
   * The full-page checkout: show one part of the forms per step (the page
   * keeps this mounted across its steps, so what is typed stays in memory).
   * Absent: every question at once, as the drawer has always shown them.
   */
  stepped?: SteppedForms;
  /**
   * The drawer on a phone (checkoutLayout's oneRiderPerScreen): the rider
   * cards one at a time behind their chips, everything else as before. Each
   * rider is then saved as the guest goes, exactly as on the full page
   * (Next, Back or a chip saves the rider on screen with the one-rider
   * writer), and Proceed sends only the riders not saved yet.
   */
  paged?: boolean;
  /**
   * The drawer: the hold as its checkout knows it. `ended`: the hold timer
   * ran out (its banner says so); `lapsed`: the server said the hold had
   * ended (a save, or the payment). Either way nothing more is saved and
   * the pager's Next is off. `onLapse`: a one-rider save found the hold
   * ended (not_authorized / hold_expired), so Pay stops too. `onBack`: back
   * to the visit, offered beside the hold-ended words.
   */
  hold?: { ended: boolean; lapsed: boolean; onLapse: () => void; onBack?: () => void };
  /**
   * The drawer: what its payment button is called in the words of a stop
   * that button found ("Pay", or "Confirm booking" on a cart with nothing
   * to pay): "tap Pay to try again".
   */
  payName?: string;
  /** The full-page checkout's summary, progress and Review read this. */
  onSummary?: (summary: ParticipantSummary) => void;
}) {
  const [forms, setForms] = useState<CheckoutForm[]>([]);
  const [draft, setDraft] = useState<CheckoutDraft>(EMPTY_DRAFT);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const notify = useRef(onValidityChange);
  notify.current = onValidityChange;
  const generation = useRef(0);
  const visits = useMemo(() => groupVisits(forms), [forms]);
  // The full-page checkout and the drawer's one-rider pager save each rider
  // as the guest goes (saveRider): which riders the server holds as their
  // cards show them (the ticks). Memory only, beside the draft
  // (state/rider-autosave.ts).
  const [saved, setSaved] = useState<SavedRiders>(NO_SAVED_RIDERS);
  // Fixed for the component's life: the page mounts it with `stepped`, the
  // drawer on a phone `paged`. The drawer that shows every rider at once
  // keeps today's whole-list save on Proceed — also when a resized window
  // turns it into a pager later, and a pager turned wide keeps saving one
  // rider at a time: one checkout never mixes the two writers.
  const autosave = useRef(!!stepped || paged).current;
  // The drawer's pager, saving as the guest goes.
  const pagerSaves = autosave && !stepped;
  const lapse = useRef(stepped?.onLapse ?? hold?.onLapse);
  lapse.current = stepped?.onLapse ?? hold?.onLapse;
  // The drawer: a one-rider save found the hold ended. Its words stay on the
  // card for as long as Next is off, whatever the guest taps or types.
  const [lapsedOnSave, setLapsedOnSave] = useState(false);
  // A one-rider save is on its way (saveRider), and the drawer pager's Save
  // guest details or Pay is saving (commitStepped): Pay stays off meanwhile
  // (`ready`), so a payment never starts on a rider a save may still change
  // or refuse; the Save button says "Saving…" and a second tap waits.
  const [riderSaving, setRiderSaving] = useState(false);
  const [committing, setCommitting] = useState(false);
  const committingRef = useRef(false);
  // The latest state for write(): the checkout calls commit() from its own
  // closure, which may be a render behind this one.
  const live = useRef({ forms, visits, draft, loaded, saved });
  // Declared before the load effect, so a reload merges into the latest draft.
  useEffect(() => {
    live.current = { forms, visits, draft, loaded, saved };
  });
  const savingRef = useRef(false);
  // One save at a time (so at most one per booking in flight): a write that
  // starts while another is saving waits, and goes on once the page has
  // rendered what that save left (live is refreshed first, above).
  const idleWaiters = useRef<(() => void)[]>([]);
  const alive = useRef(true);
  // Each save's number, and the last save that ended by re-reading the forms
  // (a refusal that reloads them, or "use my details" withdrawn). A write that
  // waited on that save stops with the same words instead of planning from
  // the forms as they were before the re-read.
  const saveSeq = useRef(0);
  // `rider`: the rider card (RiderSlot.key) a one-rider save's words are about.
  const interrupted = useRef<{ seq: number; message: string; rider?: string } | null>(null);
  const wakeWaiters = () => {
    for (const resolve of idleWaiters.current.splice(0)) resolve();
  };
  useEffect(() => {
    if (!savingRef.current) wakeWaiters();
  });
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      wakeWaiters();
    };
  }, []);
  // A save was refused because the people on a booking changed elsewhere:
  // the reload it started says so once it has read the forms again.
  const changedNotice = useRef(false);
  // Per rider card (RiderSlot.key), the rows sent in saves whose reply never
  // came (the signal, or the time limit): this cart's, shared with a drawer
  // closed and opened again (lostSaves). Any of them may have been stored: a
  // read that finds one there knows it for this checkout's own
  // (landedOwnSaveOf), never someone else's. Page memory only.
  const unconfirmed = useRef<Record<string, AnswerRow[]>>(lostSavesOf(cartToken));
  unconfirmed.current = lostSavesOf(cartToken);
  /**
   * The riders the server holds as one of those saves (and forgets them):
   * RiderSlot.key → the row that landed, or null when it is one of them but
   * not known which (they differ only in private answers: sent again).
   */
  function landedOwn(visitsNow: readonly CheckoutVisit[]): Record<string, AnswerRow | null> {
    const own: Record<string, AnswerRow | null> = {};
    for (const slot of riderSlots(visitsNow)) {
      const rows = unconfirmed.current[slot.key];
      if (!rows?.length) continue;
      const found = slotForm(visitsNow, slot);
      const landed = found ? landedOwnSaveOf(found.form, slot.ordinal, rows) : null;
      if (!landed) continue;
      own[slot.key] = landed.row;
      delete unconfirmed.current[slot.key];
    }
    return own;
  }
  // Rider 1 was saved with the booker's details ("Yes, I'm riding" on the
  // full page, "use my details" in the drawer), so the question has gone;
  // Your details on the page, and the drawer above Rider 1, say why.
  const [riderOneFromBooker, setRiderOneFromBooker] = useState<{ who: string; bookingId: string } | null>(null);
  // (P4a) Rider 1's list guards a saved name (a private question): the note
  // above says how a misspelt name is put right.
  const riderOneList = riderOneFromBooker ? forms.find((f) => f.booking_id === riderOneFromBooker.bookingId) : undefined;
  const riderOneNameLocked = !!riderOneList && nameRuleGuards(allRiderFields(riderOneList));
  // "Who is not coming?" (P4a): the booking whose removal is on its way, and
  // what the last one said (per booking); "Removed Jo." once done.
  const [notComingBusy, setNotComingBusy] = useState<string | null>(null);
  const [notComingError, setNotComingError] = useState<Record<string, string>>({});
  const [notComingNotice, setNotComingNotice] = useState<string | null>(null);
  // Every rider on screen at once (the desktop drawer): a save that stopped
  // to ask "Is this still {name}?" brings the first question into view and
  // gives it the focus, once it is drawn (the pager and the page aim their
  // own: focusProblem, cardAim).
  const [renameAim, setRenameAim] = useState<{ bookingId: string; ordinal: number; n: number } | null>(null);
  const renameAimSeq = useRef(0);
  useEffect(() => {
    if (!renameAim) return;
    const el = document.getElementById(`participant-${renameAim.bookingId}-${renameAim.ordinal}-rename`);
    if (!el) return;
    setRenameAim(null);
    el.scrollIntoView({ block: "center" });
    el.focus({ preventScroll: true });
  });
  // (P4a, review round) Where the focus goes once an answer changes the card
  // under it: the button pressed is gone, and the focus must never fall to
  // the page (or to the step's heading at the top). After the booker's Yes:
  // the words that replace the question; after Replace: the note on the
  // card ("New rider in place of …", with Undo); after Change it back or
  // Undo: the name put back; after "Remove": the words saying who went.
  // In a layout effect, so it lands before the page's own fallback looks
  // for a focus lost to the page.
  const [focusAim, setFocusAim] = useState<{ id: string; n: number } | null>(null);
  const focusAimSeq = useRef(0);
  const focusAimDone = useRef(0);
  const aimFocus = (id: string | null) => {
    if (id) setFocusAim({ id, n: ++focusAimSeq.current });
  };
  useLayoutEffect(() => {
    if (!focusAim || focusAim.n === focusAimDone.current) return;
    const el = document.getElementById(focusAim.id);
    if (!(el instanceof HTMLElement)) return;
    focusAimDone.current = focusAim.n;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: "nearest" });
  });

  /**
   * "Is this still {name}?" (P4a): ask it on these cards. In `live` at once
   * (a save that follows plans from it) and in the state; the card's
   * question takes the focus (focusProblem, the pager's aim).
   */
  function openRename(asks: readonly Pick<RenameAsk, "id">[], forced = false) {
    if (!asks.length) return;
    const ids = asks.map((a) => a.id);
    live.current = { ...live.current, draft: askRename(live.current.draft, ids, forced) };
    setDraft((current) => askRename(current, ids, forced));
  }

  /** An answer on one card (see answerRename); the focus goes where the card now asks (focusAim). */
  function answerRider(form: CheckoutForm, ordinal: number, answer: "booker" | "back" | "replace" | "undo") {
    setError(null);
    setNotComingNotice(null);
    if (pagerSaves) setPagerMessage(null);
    stepped?.onAnswered?.();
    setDraft((current) => answerRename(current, form, ordinal, answer));
    const prefix = `participant-${form.booking_id}-${ordinal}`;
    const name = firstNameKey(form.fields);
    aimFocus(
      answer === "booker"
        ? `${prefix}-rename`
        : answer === "replace"
          ? `${prefix}-replacing`
          : name
            ? `${prefix}-${name}`
            : `${prefix}-title`,
    );
  }

  /**
   * "Is this still {name}?" as the guest leaves a saved rider's name for
   * another answer (rider-ids spec §7.2: on blur of a marked field, or on
   * save): the question opens on the card without taking the focus, so the
   * guest sees it by the name they changed, not only once Pay sends the
   * whole list. Leaving for a button (Next, Pay, Save) is left to that
   * button's save, which asks with the focus (a question opening under a
   * pointer that is pressing would move the button away from it); so is
   * moving between the card's own name questions.
   */
  function nameLeft(form: CheckoutForm, ordinal: number, e: React.FocusEvent<HTMLElement>) {
    const from = e.target;
    const to = e.relatedTarget;
    const prefix = `participant-${form.booking_id}-${ordinal}-`;
    const keyOf = (el: EventTarget | null) =>
      el instanceof HTMLElement && el.id.startsWith(prefix) ? el.id.slice(prefix.length) : null;
    const key = keyOf(from);
    if (!key || !isNameKey(form.fields, key) || !nameRuleGuards(allRiderFields(form))) return;
    if (!(to instanceof HTMLInputElement || to instanceof HTMLSelectElement || to instanceof HTMLTextAreaElement)) return;
    const toKey = keyOf(to);
    if (toKey && isNameKey(form.fields, toKey)) return;
    const state = live.current;
    const id = participantIdOf(state.draft.rows[form.booking_id]?.[ordinal]);
    if (!id || state.draft.renameAsked?.[id] || !renameNeeded(form, state.draft, ordinal)) return;
    openRename([{ id }]);
  }

  /**
   * "Who is not coming?": each chosen rider (and any place nobody is in that
   * stands in the way) removed by id with the one-rider writer's tombstone,
   * from the end, each checked against the rider as this page read it; then
   * the forms are read again. Never a rider that was not chosen.
   */
  async function removeNotComing(form: CheckoutForm, chosen: string[]) {
    if (notComingBusy || savingRef.current) return;
    const removals = notComingRemovals(allRiderFields(form), form.participants, form.party_size, chosen, {
      withheld: form.withheld_rider,
    });
    const visit = visits.find((v) => v.forms.some((f) => f.booking_id === form.booking_id));
    const noun = visit?.noun ?? DEFAULT_NOUN;
    // Named as the chooser named them: the name and the number the page gave
    // them, so a rider with the same name who stays is never taken for them.
    const plan = notComingOfForm(form);
    const first = visit ? visit.riderStart[visit.forms.indexOf(form)]! + 1 : 1;
    const gone = plan ? notComingChosen(notComingNumbered(plan, first), chosen) : [];
    setNotComingBusy(form.booking_id);
    setNotComingError((cur) => ({ ...cur, [form.booking_id]: "" }));
    setNotComingNotice(null);
    savingRef.current = true;
    try {
      for (const r of removals) {
        const expected = (form.participants[r.number - 1] ?? {}) as Record<string, unknown>;
        const reply = await saveBookingParticipantRow(supabase, {
          bookingId: form.booking_id,
          cartToken,
          position: r.number,
          row: tombstoneFor(r.id),
          expected,
        });
        if (!alive.current) return;
        if (!reply.ok) {
          const who = riderNameOf(allRiderFields(form), expected) || `${nounTitle(noun)} ${r.number}`;
          const problem = riderSaveProblem(reply.reason, who, noun, {
            network: reply.reason === "unknown" ? reply.network : undefined,
            again: "Remove",
          });
          if (problem.kind === "lapsed") {
            lapse.current?.();
            setLapsedOnSave(true);
          }
          setNotComingError((cur) => ({
            ...cur,
            [form.booking_id]:
              problem.kind === "changed"
                ? ridersChangedMessage([who])
                : problem.kind === "lapsed" || problem.kind === "offline" || problem.kind === "server" || problem.kind === "private"
                  ? problem.message
                  : `${who} could not be removed. Please try again.`,
          }));
          // Read the forms again: the list as the server holds it now.
          if (problem.kind !== "offline" && problem.kind !== "lapsed") setReload((n) => n + 1);
          return;
        }
      }
      setNotComingNotice(notComingDone(gone, noun));
      aimFocus(NOT_COMING_DONE_ID);
      stepped?.onAnswered?.();
      if (pagerSaves) setPagerMessage(null);
      setReload((n) => n + 1);
    } catch {
      setNotComingError((cur) => ({ ...cur, [form.booking_id]: `${nounTitle(noun)} details could not be changed. Please try again.` }));
    } finally {
      savingRef.current = false;
      setNotComingBusy(null);
      setTimeout(() => {
        if (!savingRef.current) wakeWaiters();
      }, 250);
    }
  }

  useEffect(() => {
    const request = ++generation.current;
    let cancelled = false;
    setLoaded(false);
    setError(null);
    notify.current(false);
    void (async () => {
      try {
        const next = await readCartForms(cartToken);
        if (cancelled || request !== generation.current) return;
        // Keep what the guest typed (and ticked) across the reload — unless
        // the people it was typed for are no longer the ones on the booking
        // (rider numbers): then the booking shows what the server holds now.
        const previous = live.current;
        const nextVisits = groupVisits(next);
        const merged = mergeDraft(previous.draft, previous.visits, nextVisits);
        // The full page: which riders the reader returned as in (ticked),
        // and a rider changed elsewhere under unsaved typing shows the
        // server's rider, never saved over (afterRead).
        const read = autosave
          ? afterRead({
              draft: merged,
              saved: previous.saved,
              previousVisits: previous.visits,
              visits: nextVisits,
              own: landedOwn(nextVisits),
            })
          : null;
        setDraft(read?.draft ?? merged);
        if (read) setSaved(read.saved);
        setForms(next);
        setLoaded(true);
        if (read?.changed.length) setError(ridersChangedMessage(riderTitlesOf(nextVisits, read.changed)));
        else if (merged.changedElsewhere?.length || changedNotice.current) setError(PARTICIPANTS_CHANGED_NOTICE);
        changedNotice.current = false;
      } catch {
        if (!cancelled)
          setError("Guest details could not be loaded. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartToken, signature, reload]);

  // Proceed is allowed once every required answer that is SHOWN is given and
  // every statement is ticked. Unsaved answers are written by commit(). The
  // drawer's pager: never while one of its rider saves is on its way (a
  // rider_changed there shows another rider for the guest to check).
  const ready = loaded && !saving && !(pagerSaves && (riderSaving || committing)) && checkoutReady(visits, draft);
  // Also after every load (a new `forms`): the load effect reports false
  // straight away, and a fast reload can finish before `ready` ever changes.
  useEffect(() => {
    notify.current(ready);
  }, [ready, forms]);

  const noun = visits[0]?.noun ?? DEFAULT_NOUN;
  const riderOneForm = riderOne(visits);

  // While "use my details" is ticked, an edit to the booker's own fields
  // flows into the Rider 1 answers the guest has not changed by hand.
  const bookerFirst = booker?.firstName ?? "";
  const bookerLast = booker?.lastName ?? "";
  const bookerEmail = booker?.email ?? "";
  const bookerPhone = booker?.phone ?? "";
  useEffect(() => {
    const contact = { firstName: bookerFirst, lastName: bookerLast, email: bookerEmail, phone: bookerPhone };
    setDraft((current) => syncRiderOnePrefill(current, live.current.visits, contact));
  }, [bookerFirst, bookerLast, bookerEmail, bookerPhone]);

  function togglePrefill(on: boolean) {
    if (!booker) return;
    setError(null);
    setDraft((current) => setRiderOnePrefill(current, visits, booker, on));
  }

  function editRow(form: CheckoutForm, ordinal: number, key: string, value: DraftValue) {
    setError(null);
    // "Removed Ann Rider (was Rider 2)" has been read: it goes with the next edit.
    setNotComingNotice(null);
    // The pager's words about this card go once the guest starts putting it
    // right, as the page's do (the lapse words stay: lapsedOnSave).
    if (pagerSaves) setPagerMessage(null);
    setDraft((current) => {
      const rows = [...(current.rows[form.booking_id] ?? [])];
      rows[ordinal] = applyAnswer(form.fields, rows[ordinal] ?? {}, key, value);
      return {
        ...current,
        rows: { ...current.rows, [form.booking_id]: rows },
        dirtyRows: { ...current.dirtyRows, [form.booking_id]: true },
      };
    });
  }

  function editGroup(visit: CheckoutVisit, key: string, value: DraftValue) {
    const fields = visit.groupForm?.group_fields ?? [];
    setError(null);
    setDraft((current) => ({
      ...current,
      group: {
        ...current.group,
        [visit.key]: applyAnswer(fields, current.group[visit.key] ?? {}, key, value),
      },
      dirtyGroup: { ...current.dirtyGroup, [visit.key]: true },
    }));
  }

  function clearKept(target: { bookingId: string; row: number } | { visitKey: string }, key: string, on: boolean) {
    setError(null);
    if (pagerSaves && "row" in target) setPagerMessage(null);
    setDraft((current) => setCleared(current, target, key, on));
  }

  function tick(key: string, index: number, checked: boolean) {
    setError(null);
    setDraft((current) => {
      const ticks = [...(current.ticks[key] ?? [])];
      ticks[index] = checked;
      return { ...current, ticks: { ...current.ticks, [key]: ticks } };
    });
  }

  /**
   * Write what the server does not hold yet: each form's guests, then each
   * visit's once-per-booking answers, then — Proceed only, never the Save
   * button — the ticked statements.
   */
  async function write(accept: boolean, scope: SaveScope = "all"): Promise<string | null> {
    const changed = "Your booking just changed. Please check the details and try again.";
    // Proceed pressed while a save is still writing: wait for it, then carry
    // on from what it left (it may have saved everything already).
    let waitedOn = 0;
    while (savingRef.current) {
      waitedOn = saveSeq.current;
      await new Promise<void>((resolve) => idleWaiters.current.push(resolve));
    }
    if (!alive.current) return changed;
    // The save waited on re-read the forms: what `live` holds now is from
    // before that re-read, so there is nothing sound to plan from yet.
    const stopped = interrupted.current;
    if (waitedOn && stopped?.seq === waitedOn) return stopped.message;
    let state = live.current;
    if (!state.loaded) return "Guest details are still loading. Please wait a moment.";
    for (const visit of scopeVisits(state.visits, scope)) {
      const issue = visitIssue(visit, state.draft, accept);
      if (issue) return issue;
    }
    // A saved rider renamed on a list that guards it (P4a): ask "Is this
    // still {name}?" on the card before anything is sent.
    const asks = renamesToAsk(scopeVisits(state.visits, scope), state.draft);
    if (asks.length) {
      openRename(asks);
      if (!autosave) setRenameAim({ bookingId: asks[0]!.bookingId, ordinal: asks[0]!.ordinal, n: ++renameAimSeq.current });
      return renameAskMessage(asks[0]!.who, asks[0]!.name);
    }
    let plan = commitPlan(scopeVisits(state.visits, scope), state.draft);
    if (!plan.rows.length && !plan.group.length && !(accept && plan.accept.length)) return null;
    // Each row about to be sent gets a client-only key, so the ids a save
    // returns (once the server issues them) are adopted by the rows that
    // were sent. The key itself is never sent.
    const keyed = withRowKeys(state.draft, plan.rows.map((r) => r.bookingId));
    if (keyed !== state.draft) {
      const before = state.draft;
      setDraft((current) => carryRowKeys(current, before, keyed));
      state = { ...state, draft: keyed };
      live.current = state;
      plan = commitPlan(scopeVisits(state.visits, scope), keyed);
    }
    if (!accept) plan.accept = [];
    const sentKeys = new Map(plan.rows.map((r) => [r.bookingId, planRowKeys(keyed, r.bookingId)]));
    const request = generation.current;
    const nounOf = (visitKey: string) =>
      state.visits.find((v) => v.key === visitKey)?.noun ?? DEFAULT_NOUN;
    const seq = ++saveSeq.current;
    const interrupt = (message: string) => {
      interrupted.current = { seq, message };
      return message;
    };
    savingRef.current = true;
    setSaving(true);
    try {
      // "Use my details" was ticked on the forms as last read. Read them
      // again first: when Rider 1 was saved meanwhile (another tab, or the
      // venue), show that rider (mergeDraft takes the tick back) instead of
      // saving the booker's details over it.
      // (Not for the full page's "rest": it sends no rider; its riders went
      // one at a time, each checked against the rider as last read.)
      if (state.draft.prefill && scope !== "rest") {
        const fresh = await readCartForms(cartToken);
        if (request !== generation.current) return interrupt(changed);
        const freshVisits = groupVisits(fresh);
        if (!prefillStillOffered(state.draft, freshVisits)) {
          const lastRead = live.current.visits;
          setDraft((current) => mergeDraft(current, lastRead, freshVisits));
          setForms(fresh);
          return interrupt(prefillWithdrawnMessage(noun));
        }
      }
      for (const save of plan.rows) {
        const { data, error: rpcError } = await supabase.rpc("save_booking_participants", {
          p_booking_id: save.bookingId,
          p_cart_token: cartToken,
          p_participants: save.participants,
        });
        if (request !== generation.current) return interrupt(changed);
        if (rpcError || data?.ok !== true) {
          // Each refusal shows its own words; the kept private answers stay
          // kept. The people on the booking changed elsewhere (their ids no
          // longer match): this booking's edits go and the forms are read again.
          const refusal = describeRowsRefusal(state.visits, state.draft, save.bookingId, data?.reason, participantIdsOf(data));
          const message = refusal?.message ?? describeSaveRefusal(data?.reason, noun);
          // identity_changed the page's own check missed: the question opens.
          if (refusal?.ask) openRename(refusal.ask.map((id) => ({ id })), true);
          if (refusal?.reload) {
            setDraft((current) => discardRows(current, save.bookingId));
            changedNotice.current = true;
            setReload((n) => n + 1);
            return interrupt(message);
          }
          return message;
        }
        // Accepted: the server judged these answers complete. It now holds
        // the rows sent, each under the id the reply gave it (none yet: the
        // rows exactly as sent).
        const ids = participantIdsOf(data);
        const bookerId = bookerParticipantIdOf(data);
        setForms((current) =>
          current.map((form) =>
            form.booking_id === save.bookingId
              ? {
                  ...form,
                  participants: withReturnedIds(save.participants, ids),
                  complete: true,
                  ...(bookerId ? { booker_participant_id: bookerId } : {}),
                }
              : form,
          ),
        );
        setDraft((current) => {
          const adopted = adoptSavedIds(current, save.bookingId, sentKeys.get(save.bookingId) ?? [], ids);
          const dirtyRows = { ...adopted.dirtyRows };
          delete dirtyRows[save.bookingId];
          return {
            ...adopted,
            dirtyRows,
            cleared: withoutCleared(current.cleared, `r|${save.bookingId}|`),
            // Rider 1 is saved now: "use my details" is no longer offered.
            prefill: current.prefill?.bookingId === save.bookingId ? null : current.prefill,
          };
        });
      }
      for (const save of plan.group) {
        const { data, error: rpcError } = await supabase.rpc("save_booking_group_details", {
          p_booking_id: save.bookingId,
          p_cart_token: cartToken,
          p_answers: save.answers,
        });
        if (request !== generation.current) return interrupt(changed);
        if (rpcError || data?.ok !== true) return describeSaveRefusal(data?.reason, nounOf(save.visitKey));
        // The server writes the answers onto every hold of the visit.
        setForms((current) =>
          current.map((form) =>
            form.visit_key === save.visitKey
              ? { ...form, group_answers: save.answers, group_complete: true }
              : form,
          ),
        );
        setDraft((current) => {
          const dirtyGroup = { ...current.dirtyGroup };
          delete dirtyGroup[save.visitKey];
          return { ...current, dirtyGroup, cleared: withoutCleared(current.cleared, `g|${save.visitKey}|`) };
        });
      }
      for (const acceptance of plan.accept) {
        const { data, error: rpcError } = await supabase.rpc("accept_checkout_confirmations", {
          p_booking_id: acceptance.bookingId,
          p_cart_token: cartToken,
          p_version_id: acceptance.versionId,
        });
        if (request !== generation.current) return interrupt(changed);
        if (rpcError || data?.ok !== true) {
          // The venue published new wording after this hold was made: show
          // the current statements and ask for the ticks again.
          const message = describeSaveRefusal(
            data?.reason ?? "checkout_confirmations_required",
            nounOf(acceptance.visitKey),
          );
          if (data?.reason === "confirmations_changed") {
            setReload((n) => n + 1);
            return interrupt(message);
          }
          return message;
        }
        setForms((current) =>
          current.map((form) =>
            form.visit_key === acceptance.visitKey &&
            form.confirmations?.version_id === acceptance.versionId
              ? { ...form, confirmations_accepted: true }
              : form,
          ),
        );
      }
      return null;
    } catch {
      return request === generation.current
        ? `${nounTitle(noun)} details could not be saved. Please try again.`
        : interrupt(changed);
    } finally {
      savingRef.current = false;
      setSaving(false);
      // Normally the render that follows wakes a waiting write; this only
      // makes sure one never waits for a render that does not come.
      setTimeout(() => {
        if (!savingRef.current) wakeWaiters();
      }, 250);
    }
  }

  async function save() {
    // The drawer's pager: the riders not saved yet one at a time (never the
    // whole list), then the booking answers. Nothing is saved once the hold
    // is over; a second tap while it saves waits for the first.
    if (pagerSaves) {
      if (holdOver) return;
      showStop(await pagerCommit(false, DRAWER_SAVE_BUTTON), "save");
      return;
    }
    const problem = await write(false);
    if (problem) setError(problem);
  }

  /**
   * The drawer's pager: Save guest details (`accept` false) or Pay. One at a
   * time: a second Save tap does nothing (the first is still going), and a
   * payment never starts meanwhile (Pay is off; were it pressed, it stops
   * with words). The pager's words about a rider go first, so what this save
   * finds is said afresh and a retry that worked leaves no stale "we
   * couldn't save" behind.
   */
  async function pagerCommit(accept: boolean, again: string): Promise<{ message: string; rider?: number } | null> {
    if (committingRef.current)
      return accept ? { message: "Guest details are still being saved. Please wait a moment and try again." } : null;
    committingRef.current = true;
    setCommitting(true);
    setPagerMessage(null);
    setPagerNote(null);
    setSaveStopNote(null);
    try {
      return await commitStepped(accept, again);
    } finally {
      committingRef.current = false;
      setCommitting(false);
    }
  }

  /**
   * The full-page checkout: save ONE rider with the one-rider writer
   * (save_booking_participant_row), sending the rider as this page last read
   * it (`expected`: the reader's row, or the previous reply's `row`). Nothing
   * is sent for a rider still missing something (I1) or already saved as the
   * card shows it. One save at a time, shared with write(). A save that
   * takes longer than RIDER_SAVE_TIMEOUT_MS is given up as the network.
   */
  async function saveRider(index: number, opts: { again?: string } = {}): Promise<RiderSaveResult> {
    const changed = "Your booking just changed. Please check the details and try again.";
    // Another save is writing (the pager's, while Pay was pressed): wait for
    // it, then carry on from what it left.
    let waitedOn = 0;
    while (savingRef.current) {
      waitedOn = saveSeq.current;
      await new Promise<void>((resolve) => idleWaiters.current.push(resolve));
    }
    const state = live.current;
    const slot = riderSlots(state.visits)[index];
    const found = slot ? slotForm(state.visits, slot) : null;
    const who = slot && found ? riderTitle(found.visit.noun, slot) : `${nounTitle(noun)} ${index + 1}`;
    const stop = (message: string): RiderSaveResult => ({
      ok: false,
      problem: { kind: "refused", message, reload: false },
      who,
    });
    if (!alive.current) return stop(changed);
    // The save waited on read the forms again (rider_changed put another
    // rider on a card): stop with its words, as write() does, so the guest
    // checks that card before anything else is saved or paid for.
    const stopped = interrupted.current;
    if (waitedOn && stopped?.seq === waitedOn) {
      const at = stopped.rider ? riderSlots(state.visits).findIndex((s) => s.key === stopped.rider) : -1;
      return { ...stop(stopped.message), ...(at >= 0 ? { rider: at } : {}) } as RiderSaveResult;
    }
    if (!state.loaded) return stop("Guest details are still loading. Please wait a moment.");
    if (!slot || !found) return { ok: true, sent: false, who };
    // More riders saved than the booking is now for (P4a): the guest says
    // who is not coming first (the server would refuse rows_out_of_line).
    if (notComingOfForm(found.form))
      return { ok: false, problem: { kind: "invalid", message: notComingFirst(found.visit.noun), focus: "notcoming" }, who };
    const issue = riderIssue(state.visits, state.draft, slot);
    if (issue) return { ok: false, problem: { kind: "incomplete", message: issue }, who };
    // A saved rider renamed on a list that guards it (P4a): "Is this still
    // {name}?" on the card, nothing sent until it is answered.
    if (renameNeeded(found.form, state.draft, slot.ordinal)) {
      const ask = renamesToAsk(state.visits, state.draft, [slot.bookingId]).find((a) => a.ordinal === slot.ordinal);
      if (ask) openRename([ask]);
      return { ok: false, problem: { kind: "invalid", message: renameAskMessage(who, ask?.name ?? ""), focus: "rename" }, who };
    }
    if (isRiderSaved(state.visits, state.draft, state.saved, slot)) return { ok: true, sent: false, who };
    // Nothing typed on an all-optional card, nothing cleared, nothing
    // stored: nothing to send (the writer would refuse the empty row). The
    // guest moves on, and the rider is ticked: nothing more to do for it.
    if (riderHasNothingToSend(state.visits, state.draft, slot)) {
      markPassed([slot]);
      return { ok: true, sent: false, who };
    }
    const request = generation.current;
    const seq = ++saveSeq.current;
    const riderNoun = found.visit.noun;
    // The row on its way. The cards stay usable while it is (no flash of
    // disabled inputs on every Next); a change typed meanwhile is caught
    // here (`edited`) and the page stays on the card instead of announcing
    // "saved" over it.
    let sent: AnswerRow = {};
    const edited = () => {
      const now = live.current;
      const at = slotForm(now.visits, slot);
      return !at || rowFingerprint(riderRowPayload(at.form, now.draft, slot.ordinal)) !== rowFingerprint(sent);
    };
    savingRef.current = true;
    setRiderSaving(true);
    try {
      // Twice at most: again only when the first was refused rider_changed
      // and the rider there now is this page's own earlier save, whose reply
      // was lost, while the card has changed since.
      for (let attempt = 0; attempt < 2; attempt++) {
        let before = live.current;
        const at = slotForm(before.visits, slot);
        if (!at) return stop(changed);
        // The row goes with a client-only key (never sent), so the rider id
        // the reply names (20261001110020) is written onto the draft row that
        // was sent, found by that key, never by where it sits by then.
        const keyed = withRowKeys(before.draft, [slot.bookingId]);
        if (keyed !== before.draft) {
          const was = before.draft;
          setDraft((current) => carryRowKeys(current, was, keyed));
          before = { ...before, draft: keyed };
          live.current = before;
        }
        const sentKey = rowKeyOf(before.draft.rows[slot.bookingId]?.[slot.ordinal]);
        sent = riderRowPayload(at.form, before.draft, slot.ordinal);
        // Replace (P4a): the card is a new rider typed in place of a saved
        // one. ONE save names the saved rider it replaces (replaceSaveRow):
        // the server deletes their answers, private ones included, and
        // stores the new rider whole under a fresh id in the same statement,
        // or stores nothing (review round: two calls could leave a place for
        // nobody in a list the payment gate had passed). Checked against the
        // saved rider as this page read it (`expected`).
        const cardRow = before.draft.rows[slot.bookingId]?.[slot.ordinal];
        const replacing = participantIdOf(cardRow) === null ? replacedIdOf(cardRow) : null;
        const wire = replacing ? replaceSaveRow(sent, replacing) : sent;
        // Until a reply says otherwise, this row may land.
        (unconfirmed.current[slot.key] ??= []).push(sent);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), RIDER_SAVE_TIMEOUT_MS);
        const reply = await saveBookingParticipantRow(supabase, {
          bookingId: slot.bookingId,
          cartToken,
          position: slot.ordinal + 1,
          row: wire,
          expected: expectedRow(at.form, slot.ordinal),
          signal: controller.signal,
        }).finally(() => clearTimeout(timer));
        if (!alive.current || request !== generation.current) return stop(changed);
        if (reply.ok) {
          delete unconfirmed.current[slot.key];
          // The server holds this rider now, exactly as sent, under the id
          // its reply names: the draft row that was sent takes it (a new
          // rider's first save), so every later save of the rider carries
          // it. The same change goes to `live` at once, so a save that
          // follows before the next render (the end of the step saves the
          // rest one after another) plans from it. The mark is the sent row
          // as the page builds it from here on (sentAsSaved), judged on the
          // state it was sent from.
          const riderId = participantIdOfReply(reply);
          const adopt = (d: CheckoutDraft) =>
            riderId && sentKey ? adoptSavedIds(d, slot.bookingId, [sentKey], [riderId]) : d;
          sent = sentAsSaved(at.form, adopt(before.draft), slot.ordinal, reply.row, reply.outstanding);
          const row = sent;
          const applyForms = (list: CheckoutForm[]) =>
            list.map((f) =>
              f.booking_id === slot.bookingId ? formAfterRiderSaved(f, slot.ordinal, reply.row, reply.outstanding) : f,
            );
          const cur = live.current;
          if (cur.draft.prefill?.bookingId === slot.bookingId && slot.ordinal === 0)
            setRiderOneFromBooker({ who, bookingId: slot.bookingId });
          const forms2 = applyForms(cur.forms);
          live.current = {
            ...cur,
            forms: forms2,
            visits: groupVisits(forms2),
            draft: draftAfterRiderSaved(adopt(cur.draft), slot.bookingId, slot.ordinal),
            saved: markSaved(cur.saved, slot, row),
          };
          setForms(applyForms);
          setDraft((current) => draftAfterRiderSaved(adopt(current), slot.bookingId, slot.ordinal));
          setSaved((current) => markSaved(current, slot, row));
          setError(null);
          return { ok: true, sent: true, who, edited: edited() };
        }
        // identity_changed the page's own check missed (a name it cannot
        // read): the server's verdict wins, the card asks (P4a). Nothing to
        // read again: the rider is as it was.
        if (reply.reason === "identity_changed") {
          const list = unconfirmed.current[slot.key] ?? [];
          const mine = list.lastIndexOf(sent);
          if (mine >= 0) list.splice(mine, 1);
          if (list.length === 0) delete unconfirmed.current[slot.key];
          const id = participantIdOf(sent);
          if (id) openRename([{ id }], true);
          return { ok: false, problem: riderSaveProblem(reply.reason, who, riderNoun), who, edited: edited() };
        }
        // A reply came: this row was not stored (an earlier one may have been).
        if (reply.reason !== "unknown") {
          const list = unconfirmed.current[slot.key] ?? [];
          const mine = list.lastIndexOf(sent);
          if (mine >= 0) list.splice(mine, 1);
          if (list.length === 0) delete unconfirmed.current[slot.key];
        }
        // A clear (or an emptied all-optional card) where the server holds no
        // answer for this rider: nothing to clear, the rider is as the card
        // shows it. In, as sent.
        if (nothingHeldToClear(reply.reason, sent)) {
          const row = sent;
          live.current = { ...live.current, saved: markSaved(live.current.saved, slot, row) };
          setSaved((current) => markSaved(current, slot, row));
          setError(null);
          return { ok: true, sent: true, who, edited: edited() };
        }
        const problem = riderSaveProblem(reply.reason, who, riderNoun, {
          network: reply.reason === "unknown" ? reply.network : undefined,
          again: opts.again,
        });
        if (problem.kind === "lapsed") {
          lapse.current?.();
          setLapsedOnSave(true);
        }
        if (problem.kind === "refused" && problem.reload) setReload((n) => n + 1);
        if (problem.kind !== "changed" && problem.kind !== "invalid") return { ok: false, problem, who, edited: edited() };
        // Read the forms again. rider_changed: this rider's card shows what
        // the server holds now, never saved over (afterRead's `force`),
        // unless that is this page's own earlier save; the other riders keep
        // their typing. invalid: the question definitions as the server has
        // them, so the page can say what is missing.
        let fresh: CheckoutForm[];
        try {
          fresh = await readCartForms(cartToken);
        } catch {
          return { ok: false, problem, who, edited: edited() };
        }
        if (!alive.current || request !== generation.current) return stop(changed);
        const cur = live.current;
        const freshVisits = groupVisits(fresh);
        const own = landedOwn(freshVisits);
        // This page's own save (landedOwnSaveOf), even when which one is not
        // known: never "changed in another window".
        const mine = problem.kind === "changed" && slot.key in own;
        const read = afterRead({
          draft: mergeDraft(cur.draft, cur.visits, freshVisits),
          saved: cur.saved,
          previousVisits: cur.visits,
          visits: freshVisits,
          force: problem.kind === "changed" && !mine ? slot.key : null,
          own,
        });
        live.current = { ...cur, forms: fresh, visits: freshVisits, draft: read.draft, saved: read.saved };
        setForms(fresh);
        setDraft(read.draft);
        setSaved(read.saved);
        if (mine) {
          // The page's own save landed (its reply was lost): nobody else
          // changed this rider. In as it was sent; a card changed since goes
          // again, checked against the rider as it reads now — and so does a
          // card when it is not known which of the page's saves landed (they
          // differ only in private answers): never ticked on a guess.
          const again = riderSlots(freshVisits).find((s) => s.key === slot.key);
          if (again && isRiderSaved(freshVisits, read.draft, read.saved, again)) {
            setError(null);
            return { ok: true, sent: true, who, edited: false };
          }
          continue;
        }
        // Every rider the read replaced is named, not only this one.
        const others = read.changed.filter((k) => k !== slot.key);
        const othersSaid = others.length ? ` ${ridersChangedMessage(riderTitlesOf(freshVisits, others))}` : "";
        let message: string;
        if (problem.kind === "changed") {
          message = ridersChangedMessage(riderTitlesOf(freshVisits, [slot.key, ...others]));
        } else {
          const again = riderSlots(freshVisits).find((s) => s.key === slot.key);
          const missing = again ? riderProblem(freshVisits, read.draft, again) : null;
          message = (missing ? missing.message : problem.message) + othersSaid;
        }
        interrupted.current = { seq, message, rider: slot.key };
        return { ok: false, problem: { kind: problem.kind, message }, who };
      }
      return { ok: false, problem: riderSaveProblem("rider_changed", who, riderNoun), who };
    } catch {
      return {
        ok: false,
        problem: riderSaveProblem("unknown", who, riderNoun, { again: opts.again }),
        who,
        edited: edited(),
      };
    } finally {
      savingRef.current = false;
      setRiderSaving(false);
      setTimeout(() => {
        if (!savingRef.current) wakeWaiters();
      }, 250);
    }
  }

  /**
   * The riders the guest has moved past (or saved) that have nothing to
   * send: ticked, in `live` at once and in the state (markNothingToSend).
   */
  function markPassed(slotsNow: readonly RiderSlot[]) {
    const cur = live.current;
    const marked = markNothingToSend(cur.visits, cur.draft, cur.saved, slotsNow);
    if (marked === cur.saved) return;
    live.current = { ...cur, saved: marked };
    setSaved((current) => markNothingToSend(live.current.visits, live.current.draft, current, slotsNow));
  }

  /** Every complete rider not saved yet, one at a time (the end of the Riders step, and Pay). */
  async function saveRiders(again?: string): Promise<{ index: number; problem: RiderSaveProblem } | null> {
    // A rider a save found nothing to send for (saved already, by the save
    // it waited for, or nothing typed) is not asked again; the next one is.
    const passed = new Set<string>();
    for (let guard = riderSlots(live.current.visits).length + 1; guard > 0; guard--) {
      const state = live.current;
      if (!state.loaded) return { index: 0, problem: { kind: "refused", message: "Guest details are still loading. Please wait a moment.", reload: false } };
      const slotsNow = riderSlots(state.visits);
      const next = ridersToSave(state.visits, state.draft, state.saved, slotsNow).find((i) => !passed.has(slotsNow[i]!.key));
      if (next === undefined) break;
      const result = await saveRider(next, { again });
      if (!result.ok)
        return {
          index: result.rider ?? next,
          problem:
            result.problem.kind === "incomplete"
              ? { kind: "invalid", message: result.problem.message }
              : result.problem,
        };
      if (!result.sent) passed.add(slotsNow[next]!.key);
    }
    const state = live.current;
    const left = ridersToSave(state.visits, state.draft, state.saved)[0];
    if (left === undefined) {
      // Every rider is in: the ones with nothing to send are ticked too.
      markPassed(riderSlots(state.visits));
      return null;
    }
    // Only a rider this pass saved, changed again while a save was on its
    // way, is left here.
    const slot = riderSlots(state.visits)[left];
    const who = slot ? (riderTitlesOf(state.visits, [slot.key])[0] ?? `${nounTitle(noun)} ${left + 1}`) : `${nounTitle(noun)} ${left + 1}`;
    return { index: left, problem: { kind: "refused", message: changedWhileSavingMessage(who, again), reload: false } };
  }

  /**
   * Pay on the full page, and Proceed from the drawer's pager: every rider
   * must be complete; the ones not saved yet go one at a time (never the
   * whole list again), then the booking answers still unsaved and — `accept`
   * (Pay, never the drawer's Save button) — the ticked statements, in
   * today's order. The payment gate on the server stays the judge. Where it
   * stopped: the words, and the rider they are about (if any).
   */
  async function commitStepped(accept = true, again = "Pay"): Promise<{ message: string; rider?: number } | null> {
    const state = live.current;
    if (!state.loaded) return { message: "Guest details are still loading. Please wait a moment." };
    for (const visit of scopeVisits(state.visits, "rows")) {
      const issue = visitIssue(visit, state.draft, false);
      if (issue) {
        const missing = firstIncompleteRider(state.visits, state.draft, riderSlots(state.visits));
        return { message: issue, ...(missing >= 0 ? { rider: missing } : {}) };
      }
    }
    const left = await saveRiders(again);
    if (left) return { message: left.problem.message, rider: left.index };
    const problem = await write(accept, "rest");
    return problem ? { message: problem } : null;
  }

  /**
   * Where commitStepped stopped, shown: the drawer's pager goes to the rider
   * it is about and says it on that card (an alert). After Save guest
   * details the words take the focus, so they are scrolled into view from
   * the button far below them, and a short copy stays beside the button
   * (Pay has its own words beside it: the checkout's error). Anything else,
   * and the full page, under the forms as before.
   */
  function showStop(stop: { message: string; rider?: number } | null, from: "save" | "pay" = "pay") {
    if (!stop) return;
    if (pagerSaves && paged && stop.rider !== undefined) {
      pageTo(stop.rider, false);
      setPagerMessage(stop.message);
      if (from === "save") {
        aimCard("message");
        const state = live.current;
        const slot = riderSlots(state.visits)[stop.rider];
        const found = slot ? slotForm(state.visits, slot) : null;
        setSaveStopNote(saveStopShort(stop.message, slot && found ? riderTitle(found.visit.noun, slot) : null));
      }
      return;
    }
    setError(stop.message);
  }

  /**
   * Take the guest to the first question still missing an answer — a
   * rider's (by index) or a visit's once-per-booking question ("group") —
   * and say whether there was one to go to (the page's handle, and the
   * drawer's pager).
   */
  function focusProblem(target: { rider: number } | "group"): boolean {
    const state = live.current;
    const focusAnswer = (prefix: string, key: string) => {
      const ids = answerElementId(prefix, key);
      // A list's or a yes/no's first OPTION (an input): never its help
      // line or kept-answer note (`…-help`, `…-saved`), which come first
      // in the page and cannot take focus.
      const el =
        document.getElementById(ids.exact) ??
        document.querySelector(`input[id^="${CSS.escape(ids.option)}"]`);
      if (!(el instanceof HTMLElement)) return false;
      el.focus();
      return true;
    };
    if (target === "group") {
      for (const visit of state.visits) {
        const key = groupProblemKey(visit, state.draft);
        if (key) return focusAnswer(`group-${visit.key}`, key);
      }
      return false;
    }
    const slot = riderSlots(state.visits)[target.rider];
    // First what the card asks (P4a): "Is this still {name}?", or "Who is
    // not coming?" in place of the line's cards.
    const asking =
      (slot && document.getElementById(`participant-${slot.bookingId}-${slot.ordinal}-rename`)) ||
      (slot && document.getElementById(`participant-${slot.bookingId}-notcoming`));
    if (asking instanceof HTMLElement) {
      asking.focus();
      return true;
    }
    const problem = slot ? riderProblem(state.visits, state.draft, slot) : null;
    return !!slot && !!problem && focusAnswer(`participant-${slot.bookingId}-${slot.ordinal}`, problem.key);
  }

  // The handle the checkout calls. Refreshed after every render (write()
  // reads the latest state through `live`), cleared on unmount.
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      commit: async () => {
        if (autosave) {
          // The drawer's pager names its own payment button ("Pay",
          // "Confirm booking") in the words; the full page says "Pay".
          const stop = pagerSaves ? await pagerCommit(true, payName) : await commitStepped();
          showStop(stop);
          return stop?.message ?? null;
        }
        const problem = await write(true);
        if (problem) setError(problem);
        return problem;
      },
      reload: () => setReload((n) => n + 1),
      issue: () => {
        const state = live.current;
        if (!state.loaded) return "Guest details are still loading. Please wait a moment.";
        for (const visit of state.visits) {
          const issue = visitIssue(visit, state.draft, true);
          if (issue) return issue;
        }
        return null;
      },
      noun,
      riderIssue: (index) => {
        const state = live.current;
        const slot = riderSlots(state.visits)[index];
        return slot ? riderIssue(state.visits, state.draft, slot) : null;
      },
      firstIncompleteRider: () => {
        const state = live.current;
        return firstIncompleteRider(state.visits, state.draft, riderSlots(state.visits));
      },
      save: (scope) => write(false, scope),
      saveRider,
      saveRiders,
      riderSaved: (index) => {
        const state = live.current;
        const slot = riderSlots(state.visits)[index];
        return !!slot && isRiderSaved(state.visits, state.draft, state.saved, slot);
      },
      prefillOn: (index) => {
        const state = live.current;
        const slot = riderSlots(state.visits)[index];
        return !!slot && slot.ordinal === 0 && state.draft.prefill?.bookingId === slot.bookingId;
      },
      focusProblem,
    };
  });
  useEffect(
    () => () => {
      if (handleRef) handleRef.current = null;
    },
    [handleRef],
  );

  // ── One rider at a time (the full-page checkout, and the drawer on a phone) ──
  const slots = useMemo(() => riderSlots(visits), [visits]);
  // The drawer's own pager (the page keeps its own index, in `stepped`).
  const [pagedIndex, setPagedIndex] = useState(0);
  const [pagerMessage, setPagerMessage] = useState<string | null>(null);
  // The drawer's pager: words about the checkout rather than this card,
  // shown ABOVE the card, next to its title where the focus lands — a save
  // the network lost as the guest moved on (about the rider left), or, once
  // reopened, that the riders already done are saved. Gone on the next move.
  const [pagerNote, setPagerNote] = useState<string | null>(null);
  const oneAtATime = !!stepped || paged;
  const riderIndexRaw = stepped ? stepped.riderIndex : pagedIndex;
  const riderIndex = slots.length ? Math.min(Math.max(0, riderIndexRaw), slots.length - 1) : 0;
  const currentSlot = oneAtATime ? (slots[riderIndex] ?? null) : null;
  // Where the pager takes the guest once the card is on screen. After Back /
  // Next the card's title takes focus (a tab the guest picked keeps it: they
  // are moving along the tabs); after a lapse that turned Next off, or a
  // stop Save guest details found, the card's words take it ("message").
  // The pager stayed on a card with its words while the focus stays on Next
  // (or Back): the buttons, then the words, are scrolled into view — on a
  // small screen with large text they can land below it ("reveal").
  //
  // STATE, set in the same update as the words or the card it points at, so
  // the render that shows them is the one that acts on it. (It was a ref: an
  // effect still pending from an earlier render — a CPU-throttled phone, a
  // save that failed fast — took it, found no words yet and spent it; the
  // words then landed far above the screen with the focus left on the
  // button.) Each request is acted on once, once its target is there.
  const [cardAim, setCardAim] = useState<{ to: "title" | "message" | "reveal" | "rename" | "notcoming"; n: number } | null>(null);
  const cardAimSeq = useRef(0);
  const cardAimDone = useRef(0);
  const aimCard = (to: "title" | "message" | "reveal" | "rename" | "notcoming" | false) =>
    setCardAim(to ? { to, n: ++cardAimSeq.current } : null);
  useEffect(() => {
    if (!cardAim || cardAim.n === cardAimDone.current || !currentSlot) return;
    const { bookingId, ordinal } = currentSlot;
    if (cardAim.to === "reveal") {
      cardAimDone.current = cardAim.n;
      document.getElementById(`participant-${bookingId}-panel`)?.querySelector(".vt-rider-pager")?.scrollIntoView({ block: "nearest" });
      document.getElementById(`participant-${bookingId}-${ordinal}-message`)?.scrollIntoView({ block: "nearest" });
      return;
    }
    const target = document.getElementById(
      cardAim.to === "notcoming" ? `participant-${bookingId}-notcoming` : `participant-${bookingId}-${ordinal}-${cardAim.to}`,
    );
    if (!target) return;
    cardAimDone.current = cardAim.n;
    target.focus();
  }, [cardAim, currentSlot]);
  // Save guest details stopped on a rider: a short copy of the card's words
  // beside the button (the card can be far above it; the focus goes to the
  // card's words, but a slow or missed scroll must never leave nothing near
  // the button). Gone with the card's words.
  const [saveStopNote, setSaveStopNote] = useState<string | null>(null);
  function pageTo(index: number, focus: boolean) {
    setPagerMessage(null);
    setPagerNote(null);
    setSaveStopNote(null);
    aimCard(focus ? "title" : false);
    setPagedIndex(index);
  }
  // The drawer's pager while it saves the rider it is leaving: its buttons
  // say "Saving…" and stay focusable (aria-disabled); a second press, or a
  // chip tapped meanwhile, is ignored until the answer (as on the full page).
  // So are they while Save guest details or Pay is saving.
  const [pagerBusy, setPagerBusy] = useState(false);
  const pagerBusyRef = useRef(false);
  // Said to a screen reader as the pager moves on ("Rider 1 saved. Rider 2 of 4").
  const [pagerLive, setPagerLive] = useState("");
  // "Are you riding?" (the page's Your details step): "No" stays chosen once picked.
  const [ridingNo, setRidingNo] = useState(false);

  // The ticks (the full page, and the drawer's pager): each rider the server holds as its card shows it.
  const savedNow = useMemo(() => (autosave ? savedFlags(visits, draft, saved, slots) : []), [autosave, visits, draft, saved, slots]);

  // The drawer's pager reopens (the drawer closed and opened again, Back to
  // the visit and in again, a reload) at the first rider not saved yet, the
  // saved ones ticked from the server's reader. Once, on the first read.
  // Riders already in are said above the card (the page's resume words:
  // the contact details are never kept).
  const resumed = useRef(false);
  useEffect(() => {
    if (!pagerSaves || !paged || resumed.current || !loaded) return;
    resumed.current = true;
    const at = resumeRiderIndex(savedNow, pagedIndex);
    if (at !== pagedIndex) setPagedIndex(at);
    setPagerNote(resumeSavedNotice(noun, savedNow.filter(Boolean).length, savedNow.length));
  }, [pagerSaves, paged, loaded, savedNow, pagedIndex, noun]);

  // The drawer's hold: nothing more can be saved (the pager's Next is off).
  const holdOver = !!hold?.ended || !!hold?.lapsed || lapsedOnSave;
  // The hold ended (the timer, or a refused payment) while the pager's Next
  // had the focus: Next turns off, and the focus goes to the way on — the
  // timer banner's "Back to your visit" — never lost to the page. (A save
  // that found the hold ended puts it on the card's words: pagerStayed.)
  const pagerNextRef = useRef<HTMLButtonElement | null>(null);
  const holdWasOver = useRef(holdOver);
  // Read while rendering the change: once Next is disabled, the browser has
  // already moved the focus to the page.
  const nextLosesFocus = useRef(false);
  if (holdOver && !holdWasOver.current && pagerNextRef.current && document.activeElement === pagerNextRef.current)
    nextLosesFocus.current = true;
  useLayoutEffect(() => {
    holdWasOver.current = holdOver;
    if (!nextLosesFocus.current) return;
    nextLosesFocus.current = false;
    const next = pagerNextRef.current;
    if (!pagerSaves || lapsedOnSave || !next) return;
    const way =
      next.closest("form")?.querySelector<HTMLElement>(".vt-hold-timer-action") ??
      (currentSlot ? document.getElementById(`participant-${currentSlot.bookingId}-${currentSlot.ordinal}-title`) : null);
    way?.focus();
  }, [holdOver, pagerSaves, lapsedOnSave, currentSlot]);

  /**
   * The drawer's pager leaves the rider on screen: saved first, with the
   * page's rules and words (leaveBeforeSave, saveRider, leaveAfterSave).
   * The last rider's retry button is Save guest details (the pager has no
   * Next there: retryButton).
   */
  async function leavePaged(how: "next" | "move"): Promise<LeaveOutcome> {
    const state = live.current;
    const index = riderIndex;
    const slot = riderSlots(state.visits)[index];
    const before = leaveBeforeSave(how, {
      holdOver: !state.loaded || !slot || holdOver,
      issue: slot ? riderIssue(state.visits, state.draft, slot) : null,
    });
    if (before) return before;
    const again = retryButton("drawer", index >= riderSlots(state.visits).length - 1);
    // The last press's words go while this save is on its way (the page's
    // do too), so what the reply says is said afresh, even the same words.
    setPagerMessage(null);
    setSaveStopNote(null);
    pagerBusyRef.current = true;
    setPagerBusy(true);
    try {
      return leaveAfterSave(await saveRider(index, { again }), how, again);
    } finally {
      pagerBusyRef.current = false;
      setPagerBusy(false);
    }
  }
  /** The pager moved on: say what was saved, then where the guest is ("Rider 1 saved. Rider 2 of 4"). */
  function pagerMoved(index: number, focus: boolean, out: Extract<LeaveOutcome, { go: true }>) {
    pageTo(index, focus);
    const slot = slots[index];
    const title = slot ? riderTitlesOf(visits, [slot.key])[0] : null;
    // The save the network lost on the way: said above the card moved to,
    // by its title (where the focus lands), and to a screen reader.
    setPagerLive([out.announce, out.after, title ? `${title} of ${slots.length}` : null].filter(Boolean).join(" "));
    if (out.after) setPagerNote(out.after);
  }
  /**
   * The pager stayed on the rider: why, on its card. Focus on what is
   * missing, or on the words once Next is off; otherwise it stays on the
   * button, and the button and the words are scrolled into view.
   */
  function pagerStayed(out: Extract<LeaveOutcome, { go: false }>) {
    const problem = out.problem;
    if (!problem) return;
    setPagerMessage(problem.message);
    if (problem.kind === "invalid" && problem.focus) aimCard(problem.focus);
    else if (problem.kind === "incomplete" || problem.kind === "invalid") focusProblem({ rider: riderIndex });
    else if (problem.kind === "lapsed") aimCard("message");
    else aimCard("reveal");
  }
  async function pagerNext() {
    if (pagerBusyRef.current || committingRef.current) return;
    const out = await leavePaged("next");
    if (out.go) pagerMoved(riderIndex + 1, true, out);
    else pagerStayed(out);
  }
  async function pagerBack() {
    if (pagerBusyRef.current || committingRef.current || riderIndex === 0) return;
    const out = await leavePaged("move");
    if (out.go) pagerMoved(riderIndex - 1, true, out);
    else pagerStayed(out);
  }
  async function pagerPick(index: number) {
    if (pagerBusyRef.current || committingRef.current || index === riderIndex) return;
    const out = await leavePaged("move");
    if (out.go) pagerMoved(index, false, out);
    else pagerStayed(out);
  }

  // What the full-page checkout's summary and Review read (memory only).
  const summaryKey = useRef("");
  const summarise = useRef(onSummary);
  summarise.current = onSummary;
  useEffect(() => {
    if (!summarise.current) return;
    const summary: ParticipantSummary = {
      loaded,
      noun,
      riders: slots.map((slot, index) => ({ ...slot, ...riderReview(visits, draft, slot), saved: savedNow[index] === true })),
      bookingDetails: visits.some((v) => v.groupForm != null),
      confirmations: visits.some((v) => v.confirmations.length > 0),
    };
    const key = JSON.stringify(summary);
    if (key === summaryKey.current) return;
    summaryKey.current = key;
    summarise.current(summary);
  });

  if (loaded && !asksAnything(visits)) return null;

  const asksAnswers = visits.some(
    (v) => v.groupForm != null || v.forms.some((f) => f.fields.length > 0),
  );
  // Something is not saved yet. The drawer's pager saves riders one at a
  // time: a rider is in once it is saved as its card shows it (ridersToSave
  // is empty), whatever was edited on the way.
  const dirty =
    (pagerSaves ? ridersToSave(visits, draft, saved, slots).length > 0 : Object.keys(draft.dirtyRows).length > 0) ||
    Object.keys(draft.dirtyGroup).length > 0;
  const answersComplete = visits.every((v) => visitIssue(v, draft, false) === null);
  const heading =
    noun.one === DEFAULT_NOUN.one ? "Activity guest details" : `${nounTitle(noun)} details`;

  /**
   * One list of questions (a guest's, or the booking's): the privacy line
   * once above each run of sensitive questions (the staff line, and the
   * questions locked, for a member below manager); on an empty sensitive
   * question the server may hold, "Kept private — type to change." instead
   * of a demand, with "Clear" when it is optional.
   */
  function questionList(
    fields: QuestionField[],
    row: DraftRow,
    idPrefix: string,
    list: {
      /** The effective kept keys (the draft counted). */
      saved: ReadonlySet<string> | undefined;
      /** Private questions shown locked (staff below manager). */
      locked: boolean;
      /** A kept answer the guest cleared. */
      isCleared: (key: string) => boolean;
      onClear: (key: string, on: boolean) => void;
      onChange: (key: string, value: DraftValue) => void;
      /** Said under the privacy line of the first run of private questions. */
      privateNote?: string | null;
    },
  ) {
    const typed = typedRow(fields, row);
    const shown = (field: QuestionField) => isFieldVisible(field, typed);
    // Pairs and rows (layoutFields, over the whole form so nothing forms or
    // splits as answers show and hide questions). The privacy lines go over
    // the order the card shows: a guardian question below the age row still
    // gets its line right above it.
    const layout = layoutFields(fields);
    // Staff: a question no longer asked gets its own line, never the medical one.
    const runs = sensitiveRuns(layoutOrder(layout), shown, { byKind: list.locked });
    const one = (field: QuestionField) => {
      const run = runs.get(field.key);
      const noteId = run ? `${idPrefix}-private-${run}` : undefined;
      const locked = list.locked && field.sensitive === true;
      const empty = isEmptyAnswer(field, row[field.key]);
      const isSaved = !locked && list.saved?.has(field.key) === true && empty;
      const isCleared = !locked && !isSaved && empty && list.isCleared(field.key);
      const opensRun = run === field.key;
      const firstRun = opensRun && run === runs.values().next().value;
      return (
        <Fragment key={field.key}>
          {opensRun && (
            <p id={noteId} className="vt-q-help vt-q-private">
              {privacyNote(field, list.locked, venueName)}
            </p>
          )}
          {firstRun && list.privateNote && <p className="vt-q-help vt-q-private vt-q-adopted">{list.privateNote}</p>}
          <QuestionControl
            field={field}
            id={`${idPrefix}-${field.key}`}
            value={locked ? undefined : row[field.key]}
            onChange={(value) => list.onChange(field.key, value)}
            privateNoteId={noteId}
            disabled={locked}
            savedNote={isSaved ? savedSensitiveNote(field.type) : isCleared ? CLEARED_SENSITIVE_NOTE : null}
            noteAction={
              isSaved && !field.required
                ? { label: "Clear", ariaLabel: `Clear the kept answer to ${field.label}`, onClick: () => list.onClear(field.key, true) }
                : isCleared
                  ? { label: "Undo", ariaLabel: `Keep the answer to ${field.label}`, onClick: () => list.onClear(field.key, false) }
                  : null
            }
          />
        </Fragment>
      );
    };
    // First name + surname, and mobile + email, share a row where both fit;
    // so do up to 3 number boxes in a run (age, height, weight). .vt-field-pair
    // and .vt-q-row: the columns. A pair's and a row's questions are always shown.
    return layout.map((entry) =>
      entry.kind === "one" ? (
        shown(entry.field) ? one(entry.field) : null
      ) : (
        <div
          key={entry.fields.map((f) => f.key).join("+")}
          className={entry.kind === "row" ? "vt-field-pair vt-q-pair vt-q-row" : "vt-field-pair vt-q-pair"}
        >
          {entry.fields.map(one)}
        </div>
      ),
    );
  }

  /** "I'm riding — use my details for Rider 1": above Rider 1 only. */
  function prefillBox(visit: CheckoutVisit, form: CheckoutForm) {
    const prefillId = `participant-${form.booking_id}-prefill`;
    return (
      <label className="vt-q-choice vt-q-prefill" htmlFor={prefillId}>
        <input
          id={prefillId}
          type="checkbox"
          checked={draft.prefill?.bookingId === form.booking_id}
          onChange={(event) => togglePrefill(event.target.checked)}
        />
        <span>{prefillLabel(visit.noun, riderOneForm!.number)}</span>
      </label>
    );
  }

  /** One guest's card: "Rider 2" and their questions. */
  function riderCard(
    visit: CheckoutVisit,
    form: CheckoutForm,
    formIndex: number,
    ordinal: number,
    extra?: {
      message?: string | null;
      className?: string;
      announce?: boolean;
      saved?: boolean;
      /** A saved rider edited since: what happens to the change (in place of "Saved"). */
      pending?: string | null;
    },
  ) {
    const row = draft.rows[form.booking_id]?.[ordinal] ?? {};
    const titleId = `participant-${form.booking_id}-${ordinal}-title`;
    const who = `${nounTitle(visit.noun)} ${visit.riderStart[formIndex]! + ordinal + 1}`;
    const messageId = `participant-${form.booking_id}-${ordinal}-message`;
    // P4a: "Is this still {name}?" on a saved rider renamed; on a card that
    // replaces a saved rider, whose place it takes (and Undo).
    const stage = renameStage(form, draft, ordinal);
    const replacing = replacedIdOf(row) !== null;
    const storedName = riderNameOf(allRiderFields(form), storedRiderOf(form, row));
    return (
      <div
        className={"vt-q-rider" + (extra?.className ? ` ${extra.className}` : "")}
        role="group"
        aria-labelledby={titleId}
        aria-describedby={extra?.message ? messageId : undefined}
        onBlur={(e) => nameLeft(form, ordinal, e)}
      >
        <div id={titleId} className="vt-q-rider-title" tabIndex={oneAtATime ? -1 : undefined}>
          {who}
        </div>
        {extra?.saved ? (
          // The full page: this rider is saved as shown (the chip's tick, in words).
          <p className="vt-q-rider-saved">Saved</p>
        ) : extra?.pending && !replacing ? (
          <p className="vt-q-rider-pending">{extra.pending}</p>
        ) : null}
        {replacing && (
          <ReplacingNote
            id={`participant-${form.booking_id}-${ordinal}-replacing`}
            name={storedName}
            noun={visit.noun}
            who={who}
            onUndo={() => answerRider(form, ordinal, "undo")}
          />
        )}
        {stage && (
          <RenamePrompt
            idPrefix={`participant-${form.booking_id}-${ordinal}`}
            who={who}
            name={storedName}
            noun={visit.noun}
            stage={stage}
            marked={nameMarked(allRiderFields(form))}
            onAnswer={(answer) => answerRider(form, ordinal, answer)}
          />
        )}

        {questionList(
          form.locked_fields.length ? form.locked_fields : form.fields,
          row,
          `participant-${form.booking_id}-${ordinal}`,
          {
            saved: savedRowKeys(form, draft)[ordinal],
            locked: form.locked_fields.length > 0,
            isCleared: (key) =>
              !!draft.cleared?.[clearedKey({ bookingId: form.booking_id, row: ordinal }, key)],
            onClear: (key, on) => clearKept({ bookingId: form.booking_id, row: ordinal }, key, on),
            onChange: (key, value) => editRow(form, ordinal, key, value),
            // A rider loaded from the server after a conflict, then changed:
            // its private questions are asked again (CheckoutDraft.adopted).
            privateNote:
              autosave && form.withheld_rider && adoptedAndChanged(form, draft, ordinal) ? ADOPTED_CHANGED_NOTE : null,
          },
        )}
        {extra?.message && (
          // The drawer's pager says it here; the full page says it beside
          // its button (and here, silently, next to the card's questions).
          <p
            id={messageId}
            role={extra.announce ? "alert" : undefined}
            // The drawer: the words take the focus a Next the hold ended had.
            tabIndex={extra.announce ? -1 : undefined}
            className="vt-q-rider-message"
          >
            {extra.message}
          </p>
        )}
      </div>
    );
  }

  function groupFieldset(visit: CheckoutVisit) {
    if (!visit.groupForm) return null;
    return (
      <fieldset className="vt-q-form" disabled={saving || !loaded}>
        <legend className="vt-q-legend">Booking details</legend>
        <p className="vt-q-help">
          Asked once for the whole booking
          {visit.groupForm.service_name ? ` (${visit.groupForm.service_name})` : ""}.
        </p>
        {questionList(
          visit.groupForm.locked_group_fields.length
            ? visit.groupForm.locked_group_fields
            : visit.groupForm.group_fields,
          draft.group[visit.key] ?? {},
          `group-${visit.key}`,
          {
            saved: savedGroupKeys(visit.groupForm, draft),
            locked: visit.groupForm.locked_group_fields.length > 0,
            isCleared: (key) => !!draft.cleared?.[clearedKey({ visitKey: visit.key }, key)],
            onClear: (key, on) => clearKept({ visitKey: visit.key }, key, on),
            onChange: (key, value) => editGroup(visit, key, value),
          },
        )}
      </fieldset>
    );
  }

  function confirmFieldsets(visit: CheckoutVisit) {
    return visit.confirmations.map((block) => {
      const ticks = draft.ticks[block.key] ?? [];
      const terms = block.confirmations.terms;
      return (
        <fieldset key={block.key} className="vt-q-form vt-q-confirm" disabled={saving || !loaded}>
          <legend className="vt-q-legend">Please confirm before paying</legend>
          <p className="vt-q-help">Tick each statement to continue.</p>
          {block.confirmations.statements.map((statement, index) => (
            <label key={index} className="vt-q-choice" htmlFor={`confirm-${block.key}-${index}`}>
              <input
                id={`confirm-${block.key}-${index}`}
                type="checkbox"
                checked={ticks[index] === true}
                onChange={(event) => tick(block.key, index, event.target.checked)}
              />
              <span>{statement}</span>
            </label>
          ))}
          {terms?.text && (
            <details className="vt-q-terms">
              <summary>Read the {terms.title ?? "terms and conditions"}</summary>
              <div className="vt-q-terms-text">{terms.text}</div>
            </details>
          )}
          {terms?.url && (
            <p className="vt-q-terms-link">
              <a href={terms.url} target="_blank" rel="noopener noreferrer">
                {terms.title ?? "Terms and conditions"}
              </a>{" "}
              <span className="vt-q-hint">(opens in a new tab)</span>
            </p>
          )}
        </fieldset>
      );
    });
  }

  /** "Who is not coming?" for this line, or null when nothing stands in the way (P4a). */
  function notComingBox(visit: CheckoutVisit, form: CheckoutForm) {
    const plan = notComingOfForm(form);
    if (!plan) return null;
    // Numbered as the page numbers the cards (a visit's second line goes on
    // from its first).
    const first = visit.riderStart[visit.forms.indexOf(form)]! + 1;
    return (
      <NotComingChooser
        key={`${form.booking_id}:${form.participants.length}:${form.party_size}`}
        idPrefix={`participant-${form.booking_id}`}
        noun={visit.noun}
        plan={notComingNumbered(plan, first)}
        busy={notComingBusy === form.booking_id}
        error={notComingError[form.booking_id] || null}
        onRemove={(ids) => void removeNotComing(form, ids)}
      />
    );
  }

  /**
   * One line's riders, one card on screen: the chips (a phone) or tabs, and
   * the card of the rider on screen when that rider is on this line. The
   * drawer's pager adds Back / Next under the card; the page has its own.
   */
  function ridersOneAtATime(visit: CheckoutVisit, form: CheckoutForm, formIndex: number, compact: boolean) {
    const mine = slots
      .map((slot, index) => ({ slot, index }))
      .filter(({ slot }) => slot.bookingId === form.booking_id);
    if (mine.length === 0) return null;
    // More riders saved than the booking is now for (P4a): "Who is not
    // coming?" in place of the line's cards until it is settled.
    const excess = notComingBox(visit, form);
    if (excess) return <div className="vt-q-line">{excess}</div>;
    const onThisLine = currentSlot?.bookingId === form.booking_id;
    const panelId = `participant-${form.booking_id}-panel`;
    const select = (index: number) => {
      if (stepped) stepped.onRiderIndex(index);
      else if (pagerSaves) void pagerPick(index);
      else pageTo(index, false);
    };
    const info = stepped?.lineInfo?.(form.booking_id) ?? null;
    const lineTitle = info?.title ?? `${form.service_name}${form.option_name ? ` · ${form.option_name}` : ""}`;
    // The drawer: the lapse words stay for as long as Next is off (the hold
    // timer's own banner says it once its clock has run out).
    const message = stepped
      ? stepped.riderMessage
      : (pagerMessage ?? (lapsedOnSave && !hold?.ended ? LAPSE_MESSAGE : null));
    const last = riderIndex >= slots.length - 1;
    const again = retryButton(stepped ? "page" : "drawer", last);
    // The drawer: Rider 1 was saved with the booker's details, so "use my
    // details" has gone; above Rider 1, why.
    const riderOneNote =
      pagerSaves &&
      !riderOneForm &&
      currentSlot?.ordinal === 0 &&
      riderOneFromBooker?.bookingId === form.booking_id
        ? riderOneSavedNote(riderOneFromBooker.who, "drawer", riderOneNameLocked)
        : null;
    return (
      <div className="vt-q-line">
        <RiderTabs
          label={`${nounTitle({ one: visit.noun.other, other: visit.noun.other })} on ${lineTitle}`}
          noun={visit.noun}
          riders={mine.map(({ slot, index }) => ({
            index,
            number: slot.number,
            // A rider is ticked once it is SAVED (the full page, and the
            // drawer's pager); a pager that saves the list on Proceed (a
            // drawer resized to a phone's width) ticks it once complete.
            complete: autosave ? savedNow[index] === true : riderIssue(visits, draft, slot) === null,
          }))}
          current={riderIndex}
          compact={compact}
          idPrefix={`participant-${form.booking_id}`}
          panelId={panelId}
          onSelect={select}
          done={autosave ? "saved" : "complete"}
        />
        {onThisLine && currentSlot && (
          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={`participant-${form.booking_id}-tab-${riderIndex}`}
            className="vt-q-rider-panel"
          >
            {!stepped && !!booker && currentSlot.ordinal === 0 && riderOneForm?.form.booking_id === form.booking_id &&
              prefillBox(visit, form)}
            {riderOneNote && <p className="vt-q-help vt-q-rider-one-saved">{riderOneNote}</p>}
            {!stepped && pagerNote && <p className="vt-q-help vt-rider-pager-note">{pagerNote}</p>}
            {riderCard(visit, form, formIndex, currentSlot.ordinal, {
              message,
              className: "vt-rider-card",
              announce: !stepped,
              saved: autosave && savedNow[riderIndex] === true,
              pending:
                autosave && savedNow[riderIndex] !== true && !!saved[currentSlot.key] ? unsavedChangesNote(again) : null,
            })}
            {!stepped && pagerSaves && lapsedOnSave && !hold?.ended && hold?.onBack && (
              // A save found the hold ended: the way on, beside the words.
              <button type="button" className="vt-link vt-rider-pager-leave" data-hold-passive onClick={hold.onBack}>
                Back to your visit
              </button>
            )}
            {!stepped &&
              (pagerSaves ? (
                // Back, Next and the chips save the rider on screen first
                // (leavePaged). While that save is on its way the buttons
                // say so and keep their focus (aria-disabled); once the hold
                // is over, Next is off (Back still moves).
                <div className="vt-rider-pager">
                  <button
                    type="button"
                    className="vt-rider-pager-back"
                    disabled={riderIndex === 0}
                    aria-disabled={pagerBusy && riderIndex > 0 ? true : undefined}
                    onClick={() => void pagerBack()}
                  >
                    {pagerBusy && last ? "Saving…" : "← Back"}
                  </button>
                  <span className="vt-rider-pager-count" aria-hidden="true">
                    {nounTitle(visit.noun)} {currentSlot.number} of {slots.length}
                  </span>
                  {!last && (
                    <button
                      ref={pagerNextRef}
                      type="button"
                      className="vt-rider-pager-next"
                      disabled={holdOver}
                      aria-disabled={pagerBusy && !holdOver ? true : undefined}
                      onClick={() => void pagerNext()}
                    >
                      {pagerBusy ? "Saving…" : `Next ${visit.noun.one} →`}
                    </button>
                  )}
                </div>
              ) : (
                <div className="vt-rider-pager">
                  <button
                    type="button"
                    className="vt-rider-pager-back"
                    disabled={riderIndex === 0}
                    onClick={() => pageTo(riderIndex - 1, true)}
                  >
                    ← Back
                  </button>
                  <span className="vt-rider-pager-count" aria-hidden="true">
                    {nounTitle(visit.noun)} {currentSlot.number} of {slots.length}
                  </span>
                  {!last && (
                    <button
                      type="button"
                      className="vt-rider-pager-next"
                      onClick={() => {
                        const issue = riderIssue(visits, draft, currentSlot);
                        if (issue) {
                          setPagerMessage(issue);
                          return;
                        }
                        pageTo(riderIndex + 1, true);
                      }}
                    >
                      Next {visit.noun.one} →
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    );
  }

  const status = (
    <>
      {notComingNotice && (
        <p id={NOT_COMING_DONE_ID} role="status" tabIndex={-1} className="vt-q-help vt-q-notcoming-done">
          {notComingNotice}
        </p>
      )}
      {!loaded && !error && <p role="status">Checking guest details…</p>}
      {error && (
        <p role="alert" style={{ color: "var(--vt-error, #a52323)" }}>
          {error}
        </p>
      )}
      {!loaded && error && (
        <button type="button" className="vt-cta" onClick={() => setReload((value) => value + 1)}>
          Retry guest details
        </button>
      )}
    </>
  );

  // ── The full-page checkout: one part of the forms per step ──
  if (stepped) {
    const part = stepped.part;
    if (part === "prefill") {
      // "Are you riding?" on Your details: Yes copies the booker's details
      // into Rider 1 (and keeps them in step while they are edited above);
      // No takes back whatever was copied. Offered only while Rider 1 has
      // nothing saved (riderOne), exactly as the drawer's box.
      if (!loaded || !booker) return null;
      if (!riderOneForm) {
        // Rider 1 was saved with the booker's details on this page: say why
        // the question has gone, and where Rider 1 is changed now.
        return riderOneFromBooker ? (
          <section className="vt-participant-details vt-participant-details-stepped" aria-label="Are you one of them?">
            <p className="vt-q-help vt-q-rider-one-saved">
              {riderOneSavedNote(riderOneFromBooker.who, "page", riderOneNameLocked)}
            </p>
          </section>
        ) : null;
      }
      const one = riderOneForm.visit.noun;
      const on = draft.prefill?.bookingId === riderOneForm.form.booking_id;
      const name = `participant-${riderOneForm.form.booking_id}-riding`;
      const isRider = one.one.trim().toLowerCase() === "rider";
      return (
        <section className="vt-participant-details vt-participant-details-stepped" aria-label="Are you one of them?">
          <fieldset className="vt-q-form vt-q-riding">
            <legend className="vt-q-legend">{isRider ? "Are you riding?" : `Are you one of the ${one.other}?`}</legend>
            <label className="vt-q-choice" htmlFor={`${name}-yes`}>
              <input
                id={`${name}-yes`}
                type="radio"
                name={name}
                checked={on}
                onChange={() => {
                  setRidingNo(false);
                  togglePrefill(true);
                }}
              />
              <span>
                Yes, I&apos;m {nounTitle(one)} {riderOneForm.number}
              </span>
            </label>
            <label className="vt-q-choice" htmlFor={`${name}-no`}>
              <input
                id={`${name}-no`}
                type="radio"
                name={name}
                checked={!on && ridingNo}
                onChange={() => {
                  setRidingNo(true);
                  togglePrefill(false);
                }}
              />
              <span>No</span>
            </label>
            <p className="vt-q-help">
              Yes copies your name and contact details into {nounTitle(one)} {riderOneForm.number}; you can change them
              on the next step.
            </p>
          </fieldset>
        </section>
      );
    }
    if (part === "riders") {
      return (
        <section className="vt-participant-details vt-participant-details-stepped" aria-label={`${nounTitle(noun)} details`}>
          {notComingNotice && (
        <p id={NOT_COMING_DONE_ID} role="status" tabIndex={-1} className="vt-q-help vt-q-notcoming-done">
          {notComingNotice}
        </p>
      )}
          {!loaded && !error && <p role="status">Checking guest details…</p>}
          {visits.map((visit) =>
            visit.forms.map((form, formIndex) =>
              form.fields.length === 0 ? null : (
                <fieldset key={form.booking_id} className="vt-q-form vt-q-form-line" disabled={saving || !loaded}>
                  <legend className="vt-q-legend">
                    {stepped.lineInfo?.(form.booking_id)?.title ??
                      `${form.service_name}${form.option_name ? ` · ${form.option_name}` : ""}`}
                  </legend>
                  {stepped.lineInfo?.(form.booking_id)?.when && (
                    <p className="vt-q-help vt-q-line-when">{stepped.lineInfo(form.booking_id)!.when}</p>
                  )}
                  {ridersOneAtATime(visit, form, formIndex, stepped.compact)}
                </fieldset>
              ),
            ),
          )}
          {error && (
            <p role="alert" style={{ color: "var(--vt-error, #a52323)" }}>
              {error}
            </p>
          )}
          {!loaded && error && (
            <button type="button" className="vt-cta" onClick={() => setReload((value) => value + 1)}>
              Retry guest details
            </button>
          )}
        </section>
      );
    }
    if (part === "booking") {
      return (
        <section className="vt-participant-details vt-participant-details-stepped" aria-label="Booking details">
          {visits.map((visit) => (visit.groupForm ? <div key={visit.key} className="vt-q-visit">{groupFieldset(visit)}</div> : null))}
          {status}
        </section>
      );
    }
    // Review & pay: each rider on one line (their name, never a private
    // answer: "provided, kept private"), the booking details, then the
    // statements to tick.
    return (
      <section className="vt-participant-details vt-participant-details-stepped" aria-label="Your answers">
        {visits.map((visit) => {
          const riders = slots
            .map((slot, index) => ({ slot, index }))
            .filter(({ slot }) => slot.visitKey === visit.key);
          const bookingIssue = visit.groupForm
            ? visitIssue(scopeVisits([visit], "group")[0]!, draft, false)
            : null;
          return (
            <div key={visit.key} className="vt-q-visit">
              {riders.length > 0 && (
                <ul className="vt-review-riders" aria-label={`${nounTitle({ one: visit.noun.other, other: visit.noun.other })}`}>
                  {riders.map(({ slot, index }) => {
                    const review = riderReview(visits, draft, slot);
                    return (
                      <li key={slot.key} className="vt-review-rider" data-complete={review.complete ? "yes" : "no"}>
                        <span className="vt-review-rider-main">
                          <strong>{review.title}</strong>
                          {review.name ? ` · ${review.name}` : ""}
                          {review.complete ? "" : " · details missing"}
                        </span>
                        {review.privateProvided && <span className="vt-review-rider-private">{PRIVATE_PROVIDED}</span>}
                        {stepped.onEdit && (
                          <button
                            type="button"
                            className="vt-review-edit"
                            aria-label={`Edit ${review.title}`}
                            onClick={() => stepped.onEdit!({ step: "riders", riderIndex: index })}
                          >
                            Edit
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {visit.groupForm && (
                <p className="vt-review-booking">
                  <span>
                    <strong>Booking details</strong>
                    {bookingIssue ? " · something is missing" : " · complete"}
                  </span>
                  {stepped.onEdit && (
                    <button
                      type="button"
                      className="vt-review-edit"
                      aria-label="Edit the booking details"
                      onClick={() => stepped.onEdit!({ step: "booking" })}
                    >
                      Edit
                    </button>
                  )}
                </p>
              )}
              {confirmFieldsets(visit)}
            </div>
          );
        })}
        {status}
      </section>
    );
  }

  return (
    <section
      className="vt-participant-details"
      aria-label="Guest details for your activities"
      style={{ margin: "20px 0" }}
    >
      {/* The checkout's section style, same as "Your details" above it. */}
      <h3 className="vt-checkout-section-title">
        {asksAnswers ? heading : "Before you pay"}
      </h3>
      {notComingNotice && (
        <p id={NOT_COMING_DONE_ID} role="status" tabIndex={-1} className="vt-q-help vt-q-notcoming-done">
          {notComingNotice}
        </p>
      )}
      {!loaded && !error && <p role="status">Checking guest details…</p>}
      {pagerSaves && (
        <p className="vt-hold-sr" role="status" aria-live="polite" aria-atomic="true">
          {pagerLive}
        </p>
      )}
      {visits.map((visit) => (
        <div key={visit.key} className="vt-q-visit">
          {visit.forms.map((form, formIndex) =>
            form.fields.length === 0 ? null : (
              <fieldset key={form.booking_id} className="vt-q-form" disabled={saving || !loaded}>
                <legend className="vt-q-legend">
                  {form.service_name}
                  {form.option_name ? ` · ${form.option_name}` : ""}
                </legend>
                {paged
                  ? ridersOneAtATime(visit, form, formIndex, true)
                  : notComingBox(visit, form) ??
                    (draft.rows[form.booking_id] ?? []).map((_row, ordinal) => {
                      const offerPrefill =
                        !!booker && ordinal === 0 && riderOneForm?.form.booking_id === form.booking_id;
                      return (
                        <Fragment key={ordinal}>
                          {/* "I'm riding — use my details for Rider 1": above Rider 1 only. */}
                          {offerPrefill && prefillBox(visit, form)}
                          {riderCard(visit, form, formIndex, ordinal)}
                        </Fragment>
                      );
                    })}
              </fieldset>
            ),
          )}
          {groupFieldset(visit)}
          {confirmFieldsets(visit)}
        </div>
      ))}
      {error && (
        <p role="alert" style={{ color: "var(--vt-error, #a52323)" }}>
          {error}
        </p>
      )}
      {!loaded && error && (
        <button type="button" className="vt-cta" onClick={() => setReload((value) => value + 1)}>
          Retry guest details
        </button>
      )}
      {loaded &&
        asksAnswers &&
        (!dirty && answersComplete ? (
          <p role="status">
            {forms.every(storedAnswersValid) ? "Guest details saved." : "These details are optional."}
          </p>
        ) : pagerSaves ? (
          // The drawer's pager: saves the riders not saved yet one at a
          // time. While it does it says so and keeps its focus
          // (aria-disabled; a second tap is ignored). Nothing more can be
          // saved once the hold is over, so it goes (the words say why).
          // Above it, where it stopped in short (showStop): there with the
          // button, so a screen reader hears what lands in it; empty once
          // the card's words go.
          holdOver ? null : (
            <>
              <div className="vt-q-save-stop" role="status">
                {pagerMessage ? saveStopNote : null}
              </div>
              <button
                type="button"
                className="vt-cta"
                aria-disabled={saving || committing ? true : undefined}
                onClick={() => {
                  void save();
                }}
              >
                {saving || committing ? "Saving…" : DRAWER_SAVE_BUTTON}
              </button>
            </>
          )
        ) : (
          <button
            type="button"
            className="vt-cta"
            disabled={saving}
            onClick={() => {
              void save();
            }}
          >
            {saving ? "Saving…" : "Save guest details"}
          </button>
        ))}
    </section>
  );
}

/** The cleared marks left once a save wrote them (the prefix names the hold or visit). */
function withoutCleared(cleared: Record<string, true> | undefined, prefix: string): Record<string, true> | undefined {
  if (!cleared) return cleared;
  const next = Object.fromEntries(Object.entries(cleared).filter(([key]) => !key.startsWith(prefix)));
  return Object.keys(next).length === Object.keys(cleared).length ? cleared : (next as Record<string, true>);
}
